import { parseISO } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import { ApolloError } from '@apollo/client';
import { APP_URLS, xTenantId } from '../../consts';
import { useSnackbar } from '../snackbar';
import { formattedTimeDifference } from '../../utils/time';
import {
  currentGameSession,
  selectDisabledGamesData,
  selectGameData,
  selectGameLaunchTime,
  selectGameLaunchUrl,
} from '../../state/game';
import { Endpoint } from '../../models/api.model';
import { useWalletUpdatedSubscription } from '../../libs/graphql/baseAppAPI/subscriptions/__generated__/wallet-updated.generated';
import {
  UserTransactionsQuery,
  useUserTransactionsLazyQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/game-get-user-transactions.query.generated';
import useDestroyUserSession from '../auth/useDestroyUserSession';

export type UseGameSessionOptions = {
  delimiter?: string;
  refreshInterval?: number;
};

export type UseGameSessionReturnValue = {
  isSessionReady: boolean;
  gameTimeElapsed?: string;
  sessionTimeElapsed?: string;
  netPosition: number;
  loading: boolean;
  error?: ApolloError;
  refresh: () => void;
  handleGameStartDuringBreak: () => void;
};

export function useGameSession({
  delimiter = ':',
  refreshInterval,
}: UseGameSessionOptions = {}): UseGameSessionReturnValue {
  const game = useSelector(selectGameData);
  const navigate = useNavigate();
  const { addSnack } = useSnackbar();
  const auth = useAuth();
  const userProfileId = auth.user?.profile?.user_profile_id as string;

  const gameSession = useSelector(currentGameSession());
  const gameLaunchTime = useSelector(selectGameLaunchTime);
  const { areGamesDisabled } = useSelector(selectDisabledGamesData);
  const gameLaunchUrl = useSelector(selectGameLaunchUrl);

  const [gameTimeElapsed, setGameTimeElapsed] = useState<string>();
  const [sessionTimeElapsed, setSessionTimeElapsed] = useState<string>();
  const [isSessionReady, setSessionReady] = useState<boolean>(false);

  const [netPosition, setNetPosition] = useState<number>(0);

  // Session tracking: first valid casino/main txn defines session
  const currentSessionIdRef = useRef<string>('');
  const count = useRef<number>(0);
  const transactionCountToRefresh = game?.netPosTransactionCount ?? 2;
  const transactionRefreshDelay = Math.max(game?.netPosDelay ?? 1000, 750);
  const { logoutUser } = useDestroyUserSession();

  const isSlingo = game?.provider?.mediatorId === 'endeavour-slingo';

  useEffect(() => {
    if (gameLaunchUrl) {
      currentSessionIdRef.current = '';
      setNetPosition(0);
      count.current = 0;
    }
  }, [gameLaunchUrl]);

  const handleTransactions = (data: UserTransactionsQuery): void => {
    const edges = data?.userTransactions?.edges ?? [];

    if (!edges.length) {
      setNetPosition(0);

      return;
    }

    let total = 0;

    for (let i = 0; i < edges.length; i++) {
      const node = edges[i]?.node;

      if (!node) continue;

      const amount = Number(node.transactionAmount) || 0;
      const type = String(node.operationType ?? '')
        .trim()
        .toLowerCase();
      const sessionId = String(node.sessionContextId ?? '').trim();
      const mediatorType = String(node.mediatorType ?? '')
        .trim()
        .toLowerCase();
      const walletType = String(node.walletType ?? '').trim();

      if (!currentSessionIdRef.current && mediatorType === 'casino' && sessionId && walletType === 'main') {
        currentSessionIdRef.current = sessionId;

        if (type === 'credit') total += amount;
        else if (type === 'debit') total -= amount;
        continue;
      }

      if (mediatorType === 'casino' && walletType === 'main' && sessionId === currentSessionIdRef.current) {
        if (type === 'credit') total += amount;
        else if (type === 'debit') total -= amount;
      }
    }

    setNetPosition(total);
  };

  const checkAuth = (): void => {
    try {
      if (auth.user?.expired) {
        auth.signinSilent().then(() => refresh());
      }

      auth.startSilentRenew();
    } catch (error) {
      logoutUser();
    }
  };

  const [fetchTransactions, { loading, error }] = useUserTransactionsLazyQuery({
    variables: {
      inTenantId: xTenantId,
      inUserProfileId: userProfileId,
      inDatefromEpoch: String(new Date(gameLaunchTime as string).getTime()),
      first: 2000,
      offset: 0,
    },
    fetchPolicy: 'network-only',
    context: { endpoint: Endpoint.appAPI },
    onCompleted: handleTransactions,
    onError: checkAuth,
  });

  const refresh = (): void => {
    if (!userProfileId || !gameLaunchTime) return;

    fetchTransactions({
      variables: {
        inTenantId: xTenantId,
        inUserProfileId: userProfileId,
        inDatefromEpoch: String(new Date(gameLaunchTime as string).getTime()),
        first: 2000,
        offset: 0,
      },
      context: { endpoint: Endpoint.appAPI },
    });
  };

  useEffect(() => {
    currentSessionIdRef.current = '';

    if (userProfileId) refresh();
  }, [userProfileId, gameLaunchTime]);

  useWalletUpdatedSubscription({
    variables: { userProfileId: userProfileId },
    skip: !userProfileId,
    onData: (result) => {
      const walletData = result.data.data?.walletUpdated;
      const currentBalance = walletData?.current?.balanceAmount;
      const previousBalance = walletData?.previous?.balanceAmount;

      if (isSlingo && currentBalance != null && previousBalance != null && currentBalance < previousBalance) {
        console.log('NP: early refresh');
        setTimeout(() => refresh(), 750);

        return;
      }

      count.current += 1;

      if (count.current >= transactionCountToRefresh) {
        console.log(
          'NP: have hit transaction count, triggering refresh after delay',
          transactionCountToRefresh,
          transactionRefreshDelay
        );
        count.current = 0;
        setTimeout(() => refresh(), transactionRefreshDelay);
      }
    },
  });

  const handleGameStartDuringBreak = (): void => {
    if (areGamesDisabled) {
      addSnack({ type: 'info', message: 'You cannot play games during the mandatory break' });
      navigate(APP_URLS.home);
    }
  };

  useEffect(() => {
    if (!gameLaunchTime && !gameSession?.sessionStart) return;
    const formatTime = (time: string): string => formattedTimeDifference(new Date(), parseISO(time), true, delimiter);

    const updateElapsedTimes = (): void => {
      if (gameLaunchTime) setGameTimeElapsed(formatTime(gameLaunchTime));

      if (gameSession?.sessionStart) setSessionTimeElapsed(formatTime(gameSession.sessionStart));
    };

    const t = setInterval(updateElapsedTimes, 1000);

    updateElapsedTimes();

    return () => clearInterval(t);
  }, [gameLaunchTime, gameSession?.sessionStart, delimiter]);

  useEffect(() => {
    const ready = !!gameSession;

    if (ready !== isSessionReady) setSessionReady(ready);
  }, [gameSession]);

  const fallbackNet = gameSession ? gameSession.wins - gameSession.losses : 0;
  const finalNet = Number.isFinite(netPosition) ? netPosition : fallbackNet;

  return {
    isSessionReady,
    gameTimeElapsed,
    sessionTimeElapsed,
    netPosition: finalNet,
    loading,
    error,
    refresh,
    handleGameStartDuringBreak,
  };
}
